import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import RelatedLinks from '../components/RelatedLinks';
import StatusPill from '../components/StatusPill';

/**
 * External purchase — what the venue's own system recorded when we booked.
 *
 * Read-only. It is opened when our side and the venue's disagree, so the two
 * lists are the point: the signups this purchase covers, and every attempt made
 * to create it, with the error text of the ones that failed.
 *
 * `result` is a numeric enum, and 3 is not a member — 0,1,2,4,5,6 are. An
 * unrecognised value is shown as itself rather than as "Unknown", so a new status
 * added on the backend is visible instead of silently flattened.
 */

const ATTEMPT_RESULT = {
  0: 'Undefined',
  1: 'Pending',
  2: 'Failed',
  4: 'Success',
  5: 'Cancellation success',
  6: 'Cancellation failed'
};

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');

const ExternalPurchaseInternal = ({ api }) => {
  const { externalPurchaseId } = useParams();

  const [purchase, setPurchase] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const answer = await api.externalPurchase.getExternalPurchaseById(externalPurchaseId);
      const data = answer?.data || answer;
      if (!data || !data.id) {
        setFailed(true);
        return;
      }
      setPurchase(data);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, externalPurchaseId]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/signups">← Signups</BackToList>
        <div className="next-note next-note--error">
          Could not load this external purchase.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!purchase) return <div className="next-page next-muted">Loading…</div>;

  const signups = Array.isArray(purchase.signups) ? purchase.signups : [];
  const attempts = Array.isArray(purchase.attempts) ? purchase.attempts : [];

  const facts = [
    ['Status', purchase.status
      ? <StatusPill status={purchase.status}>{purchase.status}</StatusPill>
      : <span className="next-muted">—</span>],
    ['Price paid', purchase.pricePaid != null ? `${purchase.pricePaid} DKK` : <span className="next-muted">—</span>],
    ['Credit card used', purchase.creditCardUsed || <span className="next-muted">—</span>],
    ['Participants', purchase.numberOfParticipants ?? <span className="next-muted">—</span>],
    ['From an existing split activity', purchase.isFromExistingSplitActivity ? 'Yes' : 'No'],
    ['External activity', purchase.externalActivityId
      ? <Guid value={purchase.externalActivityId} to={`/next/external-activity/${purchase.externalActivityId}`} />
      : <span className="next-muted">—</span>],
    ['Seasonal court slot purchase', purchase.seasonalCourtSlotPurchaseId
      ? <Guid value={purchase.seasonalCourtSlotPurchaseId} href={`/seasonalcourtpurchase/${purchase.seasonalCourtSlotPurchaseId}`} />
      : <span className="next-muted">—</span>],
    ['Created at', fmtDate(purchase.createdAt)]
  ];

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/signups">← Signups</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>External purchase</h1>
          {purchase.status && <StatusPill status={purchase.status}>{purchase.status}</StatusPill>}
        </div>
        <span className="next-detail__id">{purchase.id}</span>
      </header>

      <div className="next-detail">
        <div className="next-detail__main">
          <section className="next-card">
            <div className="next-card__head"><h2>Purchase</h2></div>
            <div className="next-facts">
              {facts.map(([label, value]) => (
                <div className="next-fact" key={label}>
                  <span className="next-fact__label">{label}</span>
                  <span className="next-fact__value">{value}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head">
              <h2>Attempts</h2>
              <span className="next-muted">{attempts.length} made</span>
            </div>

            {attempts.length === 0 ? (
              <p className="next-table__note next-muted">No attempt recorded.</p>
            ) : (
              <div className="next-table-wrap">
                <table className="next-table next-table--wide next-table--stack">
                  <thead>
                    <tr>
                      <th>Attempt</th>
                      <th>Result</th>
                      <th>Started</th>
                      <th>Ended</th>
                      <th>Reference</th>
                      <th>Secret</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attempts.map(attempt => (
                      <tr key={attempt.id}>
                        <td data-label="">
                          <Guid value={attempt.id} to={`/next/external-purchase-attempt/${attempt.id}`} />
                        </td>
                        <td data-label="Result">
                          {/* An unmapped number is shown as itself. */}
                          <StatusPill status={ATTEMPT_RESULT[attempt.result] || String(attempt.result)}>
                            {ATTEMPT_RESULT[attempt.result] || `Result ${attempt.result}`}
                          </StatusPill>
                        </td>
                        <td data-label="Started" className="next-nowrap next-muted">{fmtDate(attempt.startedAt)}</td>
                        <td data-label="Ended" className="next-nowrap next-muted">{fmtDate(attempt.endedAt)}</td>
                        <td data-label="Reference">{attempt.reference || <span className="next-muted">—</span>}</td>
                        <td data-label="Secret">{attempt.secretCode || <span className="next-muted">—</span>}</td>
                        <td data-label="Error">{attempt.errorText || <span className="next-muted">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="next-card">
            <div className="next-card__head">
              <h2>Signups</h2>
              <span className="next-muted">{signups.length} covered</span>
            </div>

            {signups.length === 0 ? (
              <p className="next-table__note next-muted">No signup on this purchase.</p>
            ) : (
              <div className="next-table-wrap">
                <table className="next-table next-table--wide next-table--stack">
                  <thead>
                    <tr>
                      <th>Signup</th>
                      <th>User</th>
                      <th>Status</th>
                      <th>Booking</th>
                      <th>Cancellation</th>
                      <th>Reference</th>
                      <th className="next-table__right">Participants</th>
                    </tr>
                  </thead>
                  <tbody>
                    {signups.map(signup => (
                      <tr key={signup.id}>
                        <td data-label=""><Guid value={signup.id} to={`/next/signup/${signup.id}`} /></td>
                        <td data-label="User" className="next-break">
                          {signup.userId
                            ? <Link className="next-link" to={`/next/user/${signup.userId}`}>{signup.userEmail || 'user'}</Link>
                            : (signup.userEmail || <span className="next-muted">—</span>)}
                        </td>
                        <td data-label="Status">
                          {signup.status
                            ? <StatusPill status={signup.status}>{signup.status}</StatusPill>
                            : <span className="next-muted">—</span>}
                        </td>
                        <td data-label="Booking">
                          {signup.bookStatus
                            ? <StatusPill status={signup.bookStatus}>{signup.bookStatus}</StatusPill>
                            : <span className="next-muted">—</span>}
                        </td>
                        <td data-label="Cancellation">
                          {signup.cancellationStatus
                            ? <StatusPill status={signup.cancellationStatus}>{signup.cancellationStatus}</StatusPill>
                            : <span className="next-muted">—</span>}
                        </td>
                        <td data-label="Reference">{signup.reference || <span className="next-muted">—</span>}</td>
                        <td data-label="Participants" className="next-table__right">
                          {signup.numberOfParticipants ?? <span className="next-muted">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                purchase.externalActivityId && {
                  label: 'External activity',
                  to: `/next/external-activity/${purchase.externalActivityId}`
                },
                signups[0]?.id && {
                  label: 'First signup',
                  hint: signups[0].userEmail,
                  to: `/next/signup/${signups[0].id}`
                },
                purchase.seasonalCourtSlotPurchaseId && {
                  label: 'Seasonal court purchase',
                  href: `/seasonalcourtpurchase/${purchase.seasonalCourtSlotPurchaseId}`
                }
              ]}
            />
          </section>

          {/* No Actions panel: the old page has none. This is a record of what
              the venue's system did, not something to change from here. */}
        </aside>
      </div>
    </div>
  );
};

ExternalPurchaseInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const ExternalPurchase = withApi(ExternalPurchaseInternal);

ExternalPurchase.requiresLogin = true;

export default ExternalPurchase;
